import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { THEME } from "@/flexx/state";
import { COMMUNITY } from "@/flexx/capstoneData";
import NextGenCTA from "@/flexx/NextGenCTA";
import { FlexxCertificate, TIER_NAME_BY_NUMBER, TIER_COURSE_COPY, openCertificatePrintView } from "@/flexx/FlexxCertificate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CertificateVerifyPage() {
  const { certId } = useParams();
  const [status, setStatus] = useState("loading");
  const [cert, setCert] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/verify/${certId}`);
        if (cancelled) return;
        setCert(res.data);
        setStatus(res.data?.valid ? "valid" : "invalid");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? "Certificate not found" : "Unable to verify certificate");
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [certId]);

  return (
    <div data-testid="cert-verify-page" style={{
      minHeight: "100vh", background: "#0d0d0d", color: "#f0f0f0",
      fontFamily: "'Inter', system-ui, sans-serif",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{ maxWidth: 620, width: "100%" }}>
        {status === "loading" && (
          <div className="flexx-card" style={{ textAlign: "center", padding: 40 }}>
            <div className="flexx-spinner" style={{ margin: "0 auto 16px" }} />
            <div style={{ color: THEME.mute, fontSize: 13 }}>Verifying certificate…</div>
          </div>
        )}

        {status === "error" && (
          <div data-testid="cert-verify-error" className="flexx-card" style={{ textAlign: "center", padding: 32, borderColor: `${THEME.red}70` }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>✗</div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.red, fontFamily: "monospace" }}>NOT FOUND</div>
            <h2 style={{ color: THEME.text, fontSize: 22, margin: "8px 0 12px" }}>{error}</h2>
            <p style={{ color: THEME.mute, fontSize: 13, lineHeight: 1.6 }}>
              The certificate ID <code style={{ color: THEME.gold }}>{certId}</code> does not exist in the FLEXX Academy registry.
            </p>
            <a href="/" className="flexx-btn-ghost" style={{ marginTop: 18, textDecoration: "none", display: "inline-block" }}>Go home</a>
          </div>
        )}

        {status === "invalid" && (
          <div data-testid="cert-verify-invalid" className="flexx-card" style={{ textAlign: "center", padding: 32, borderColor: `${THEME.red}70`, background: `${THEME.red}15` }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>⚠</div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.red, fontFamily: "monospace" }}>INVALID SIGNATURE</div>
            <h2 style={{ color: THEME.text, fontSize: 22, margin: "8px 0 12px" }}>This certificate failed verification</h2>
            <p style={{ color: THEME.mute, fontSize: 13, lineHeight: 1.6 }}>
              The record exists but its cryptographic signature is invalid or the certificate has been revoked. Contact FLEXX Academy to investigate.
            </p>
          </div>
        )}

        {status === "valid" && cert && (
          <>
            {(() => {
              const isCourse = cert.kind === "course";
              // For course certs, use the stored human tier label (foundation/intermediate)
              // falling back to the legacy tier-number mapping for older capstone certs.
              const tierName = isCourse
                ? (cert.tier_label || "foundation")
                : (TIER_NAME_BY_NUMBER[cert.tier || 1] || "foundation");
              const fallbackCopy = TIER_COURSE_COPY[tierName] || TIER_COURSE_COPY.foundation;
              const courseName = isCourse && cert.course_name ? cert.course_name : fallbackCopy.courseName;
              const courseSubtitle = isCourse && cert.course_subtitle ? cert.course_subtitle : fallbackCopy.courseSubtitle;
              const dateStr = new Date(cert.issued_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
              return (
                <>
                  <FlexxCertificate
                    studentName={cert.user_name}
                    courseName={courseName}
                    courseSubtitle={courseSubtitle}
                    completionDate={dateStr}
                    verifyCode={cert.cert_id}
                    tier={tierName}
                    onDownload={() => openCertificatePrintView({
                      studentName: cert.user_name, tier: tierName,
                      courseName, courseSubtitle,
                      completionDate: dateStr, verifyCode: cert.cert_id,
                    })} />
                  {/* Hidden testid anchors retained for E2E */}
                  <div data-testid="cert-verify-card" style={{ display: "none" }} />
                  <div data-testid="cert-verify-name" style={{ display: "none" }}>{cert.user_name}</div>
                  <div data-testid="cert-verify-issued" style={{ display: "none" }}>{dateStr}</div>
                  <div data-testid="cert-verify-id" style={{ display: "none" }}>{cert.cert_id}</div>
                  <div data-testid="cert-verify-badge" style={{ display: "none" }}>{cert.badge || "FLEXX CERTIFIED"}</div>
                </>
              );
            })()}
            <div style={{ textAlign: "center", marginTop: 28 }}>
              <a href={COMMUNITY.discord} target="_blank" rel="noopener noreferrer" data-testid="cert-verify-discord" className="flexx-btn-primary" style={{ textDecoration: "none", marginRight: 8 }}>
                Join the Certified Community →
              </a>
              <a href="/" className="flexx-btn-ghost" style={{ textDecoration: "none" }}>
                Visit FLEXX Academy
              </a>
            </div>
            <NextGenCTA variant="verify" studentName={cert.user_name || ""} />
          </>
        )}
      </div>
    </div>
  );
}